import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, wsUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FleetCtx = createContext(null);

export function FleetProvider({ children }) {
  const { user, isDemo } = useAuth();
  const [vehicles, setVehicles] = useState({});
  const [liveEvents, setLiveEvents] = useState([]);
  const [unread, setUnread] = useState(0);
  const [connected, setConnected] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [tick, setTick] = useState(0);
  const [commandTick, setCommandTick] = useState(0);

  const load = useCallback(async () => {
    const { data } = await api.get("/vehicles");
    setVehicles(Object.fromEntries(data.map((v) => [v.id, v])));
    setLoaded(true);
  }, []);

  const loadUnread = useCallback(async () => {
    const { data } = await api.get("/notifications/unread-count");
    setUnread(data.count);
  }, []);

  useEffect(() => {
    if (!user) return undefined;

    load();
    loadUnread();

    // Demo hanya memakai adapter lokal; jangan sambungkan WebSocket produksi.
    if (isDemo) {
      setConnected(true);
      const demoInterval = setInterval(() => {
        load();
        setTick((t) => t + 1);
      }, 2000);

      return () => {
        clearInterval(demoInterval);
        setConnected(false);
      };
    }

    setConnected(false);
    let ws;
    let retry;
    let ping;
    let alive = true;
    const connect = () => {
      ws = new WebSocket(wsUrl());
      ws.onopen = () => {
        setConnected(true);
        ping = setInterval(() => ws.readyState === 1 && ws.send("ping"), 25000);
      };
      ws.onclose = () => {
        setConnected(false);
        clearInterval(ping);
        if (alive) retry = setTimeout(connect, 3000);
      };
      ws.onmessage = (m) => {
        const msg = JSON.parse(m.data);
        if (msg.type === "position") setVehicles((p) => ({ ...p, [msg.vehicle.id]: msg.vehicle }));
        else if (msg.type === "vehicles_changed") load();
        else if (msg.type === "command") setCommandTick((t) => t + 1);
        else if (msg.type === "event") {
          setLiveEvents((p) => [msg.event, ...p].slice(0, 60));
          setUnread((u) => u + 1);
          const e = msg.event;
          if (e.priority === "CRITICAL") toast.error(`${e.title} · ${e.vehicle_name}`, { description: e.message });
          else if (e.priority === "WARNING") toast.warning(`${e.title} · ${e.vehicle_name}`, { description: e.message });
        }
      };
    };
    connect();
    const iv = setInterval(() => setTick((t) => t + 1), 15000);
    return () => {
      alive = false;
      clearTimeout(retry);
      clearInterval(iv);
      clearInterval(ping);
      if (ws) ws.close();
    };
  }, [user, isDemo, load, loadUnread]);

  const list = useMemo(
    () => Object.values(vehicles).sort((a, b) => a.name.localeCompare(b.name)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vehicles, tick]
  );

  const value = { vehicles: list, byId: vehicles, liveEvents, unread, setUnread, loadUnread, connected, loaded, reload: load, tick, commandTick, bumpCommands: () => setCommandTick((t) => t + 1) };
  return <FleetCtx.Provider value={value}>{children}</FleetCtx.Provider>;
}

export const useFleet = () => useContext(FleetCtx);
